/**
 * 达人订单相关接口（C端用户侧）
 * 对应 Apipost: 移动用户端 > 达人 > 达人订单
 * 使用 peiwantHttp 实例（BLW 旧项目域名）
 */

import { peiwantHttp } from "@/http/axios";
import type {
  ExpertBookTimeDataItem,
  ExpertBookTimeQuery,
} from "@/pages/expert-confirm/types";

// ==================== 类型定义 ====================

/** order/calculate 请求参数 */
export interface CalculateOrderParams {
  /** 服务项目ID */
  item_id: string | number;
  /** 优惠券ID */
  coupon_id: string | number;
  /** 用户选中的地址ID */
  user_address_id: string | number;
  /** 达人ID */
  staff_id: string | number;
  /** 出行方式ID */
  travel_mode_id: string | number;
  /** 服务数量（小时单=小时数，天单=天数） */
  num: string | number;
  /** 下单类型：1=小时单，2=日单 */
  accept_order_type: string | number;
}

/** calculate 响应中的价格数据 */
export interface CalculatePriceData {
  /** 订单金额（最终需付） */
  price: string;
  /** 服务费用 */
  item_price: string;
  /** 优惠券抵扣金额 */
  coupon_price: string;
  /** 车费/出行费 */
  fare_price: string | number;
  /** 用户与达人的实际距离(km) */
  travel_km: number;
  /** 计费距离(向上取整km) */
  fee_km: number;
  /** 原价（折扣前） */
  original_price: string;
  /** 折扣优惠金额 */
  discount_price: string;
  /** 可用优惠券列表 */
  coupon_list: CouponItem[];
}

/** 优惠券项 */
export interface CouponItem {
  id: number;
  name: string;
  money: string;
  // ... 其他优惠券字段按实际返回补充
}

/** order/calculate 完整响应 */
export interface CalculateOrderResponse {
  status: number;
  msg: string;
  data: CalculatePriceData;
}

// ==================== 接口调用 ====================

/**
 * 订单金额计算
 * POST /api/massage/order/calculate
 * Body格式: form-data
 *
 * 用于确认订单页实时计算价格（含服务费+车费+优惠券折扣）
 * 当用户切换服务项目/时间/地址/优惠券时调用此接口刷新价格
 */
export async function calculateOrderPrice(
  params: CalculateOrderParams,
): Promise<CalculatePriceData> {
  console.log("[API] 订单金额计算:", "/api/massage/order/calculate", params);

  // 使用 FormData 提交（与 address/edit 等旧项目接口保持一致）
  const formData = new FormData();
  formData.append("item_id", String(params.item_id));
  formData.append("coupon_id", String(params.coupon_id));
  formData.append("user_address_id", String(params.user_address_id));
  formData.append("staff_id", String(params.staff_id));
  formData.append("travel_mode_id", String(params.travel_mode_id));
  formData.append("num", String(params.num));
  formData.append("accept_order_type", String(params.accept_order_type));

  const res = await peiwantHttp.post<CalculateOrderResponse>(
    "/api/massage/order/calculate",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );

  console.log("[API] order/calculate 响应:", { status: res.status, data: res.data });

  const body = res.data as CalculateOrderResponse;
  if (body?.status !== 200) {
    throw new Error(body?.msg || "价格计算失败");
  }

  return body?.data;
}

// ==================== 获取收银台 ====================

/** 收银台 - 支付方式项 */
export interface CashierPayMode {
  /** 支付方式名称 */
  name: string;
  /** 图标标识 */
  icon: string;
  /** 支付方式值（weixin/yue） */
  value: string;
  /** 支付方式描述 */
  title: string;
  /** 是否可用 */
  payStatus: boolean;
  /** 余额金额（仅余额支付时有值） */
  number?: string;
}

/** 收银台 - 订单信息 */
export interface CashierOrderInfo {
  /** 订单ID */
  id: number;
  /** 需付金额 */
  pay_price: string;
  /** 下单时间戳 */
  add_time: number;
  /** 时间戳 */
  time: number;
  /** 关联订单号 */
  order_id: number;
}

/** cashier 完整响应 */
export interface CashierResponse {
  status: number;
  msg: string;
  data: {
    pay_mode: CashierPayMode[];
    order: CashierOrderInfo;
  };
}

/** cashier 请求参数 */
export interface CashierParams {
  /** 订单ID */
  order_id: string | number;
  /** 订单类型：1=服务onsite, 2=打赏reward, 3=加钟sub */
  form_type: string | number;
}

/**
 * 获取收银台（支付方式 + 订单信息）
 * POST /api/massage/cashier
 * Body格式: form-data
 *
 * 创建订单成功后调用此接口获取可用的支付方式和最终订单金额，
 * 然后携带返回数据跳转支付页
 *
 * @example
 * const cashier = await getCashier({ order_id: 123, form_type: 1 });
 * // cashier.order.pay_price → 实际需付金额
 * // cashier.pay_mode → 可用支付方式列表
 */
export async function getCashier(
  params: CashierParams,
): Promise<CashierResponse["data"]> {
  console.log("[API] 获取收银台:", "/api/massage/cashier", params);

  const formData = new FormData();
  formData.append("order_id", String(params.order_id));
  formData.append("form_type", String(params.form_type));

  const res = await peiwantHttp.post<CashierResponse>(
    "/api/massage/cashier",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );

  console.log("[API] cashier 响应:", { status: res.status, data: res.data.data });

  if (res.data.status !== 200 || !res.data.data) {
    throw new Error(res.data?.msg || "获取收银台失败");
  }

  return res.data.data;
}

// ==================== 获取预约时间 ====================

/** book_time 完整响应 */
export interface BookTimeResponse {
  status: number;
  msg: string;
  data: ExpertBookTimeDataItem[];
}

/**
 * 获取达人可预约时间段
 * GET /api/massage/book_time
 *
 * 返回多月数据，每月包含日期列表和每天的可预约状态
 * 用于确认订单页的时间选择器
 *
 * @example
 * // 小时单
 * const days = await getBookTime({ staff_id: 3, item_id: 9, accept_order_type: 1 });
 * // 日单
 * const days = await getBookTime({ staff_id: 3, item_id: 9, accept_order_type: 2 });
 */
export async function getBookTime(
  params: ExpertBookTimeQuery,
): Promise<ExpertBookTimeDataItem[]> {
  console.log("[API] 获取预约时间:", "/api/massage/book_time", params);

  const res = await peiwantHttp.get<BookTimeResponse>(
    "/api/massage/book_time",
    { params },
  );

  console.log("[API] book_time 响应:", { status: res.status, dataLen: res.data.data?.length });

  if (res.data.status !== 200 || !Array.isArray(res.data.data)) {
    throw new Error(res.data?.msg || "获取预约时间失败");
  }

  return res.data.data;
}

// ==================== 达人订单支付 ====================

/** 微信JSAPI支付配置参数 */
export interface BlwJsapiConfig {
  appId: string;
  nonceStr: string;
  package: string; // "prepay_id=wx..."
  signType: string; // "MD5"
  paySign: string;
  timestamp: string;
}

/** order/pay 响应中的 result 数据 */
export interface BlwPayResult {
  jsConfig: BlwJsapiConfig;
  ordersn: string;
  pay_key: string;
  pay_url: string;
}

/** order/pay 完整响应 */
export interface OrderPayResponse {
  status: number;
  msg: string;
  data: {
    status: string; // "WECHAT_PAY"
    result: BlwPayResult;
  };
}

/** order/pay 请求参数 */
export interface OrderPayParams {
  /** 订单ID */
  order_id: string | number;
  /** 支付类型：weixin */
  pay_type?: string;
  /** 订单类型：onsite(按摩) / reward(打赏) / sub(加钟) */
  form_type?: string;
  /** 支付完成回调跳转地址（可为空字符串） */
  quitUrl?: string;
}

/**
 * 达人订单支付
 * POST /api/massage/order/pay
 * Body格式: form-data
 * Header: Form-type: wechat
 *
 * 创建达人订单后调用此接口发起微信支付，
 * 返回的 jsConfig 用于调起 WeixinJSBridge 收银台
 *
 * @example
 * const payData = await expertOrderPay({ order_id: 116 });
 * // payData.result.jsConfig → { appId, nonceStr, package, signType, paySign, timestamp }
 * await invokeWxJsapiPay(payData.result.jsConfig);
 */
export async function expertOrderPay(
  params: OrderPayParams,
): Promise<OrderPayResponse["data"]> {
  console.log("[API] 达人订单支付:", "/api/massage/order/pay", params);

  const formData = new FormData();
  formData.append("order_id", String(params.order_id));
  formData.append("pay_type", params.pay_type || "weixin");
  formData.append("form_type", params.form_type || "onsite");
  formData.append("quitUrl", params.quitUrl || "");

  const res = await peiwantHttp.post<OrderPayResponse>(
    "/api/massage/order/pay",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        "Form-type": "wechat",
      },
    },
  );

  console.log("[API] order/pay 响应:", { status: res.status, data: res.data });

  const body = res.data as OrderPayResponse;
  if (body?.status !== 200 || !body?.data?.result?.jsConfig) {
    throw new Error(body?.msg || "创建支付失败");
  }

  return body.data;
}

/**
 * 取消达人订单
 * GET /api/orderCancel/{id}
 *
 * @example
 * await cancelExpertOrder(116);
 */
export async function cancelExpertOrder(
  id: number | string,
): Promise<{ status: number; msg: string }> {
  console.log("[API] 取消达人订单:", `/api/orderCancel/${id}`);

  const res = await peiwantHttp.get<{ status: number; msg: string }>(
    `/api/orderCancel/${id}`,
  );

  console.log("[API] orderCancel 响应:", res.data);

  if (res.data?.status !== 200) {
    throw new Error(res.data?.msg || "取消订单失败");
  }

  return res.data;
}

/**
 * C端用户侧 - 达人订单详情
 * 接口: GET /api/massage/order/detail/{id}
 * 使用 peiwantHttp（BLW 主系统域名，无 staffapi 前缀）
 */
export async function getExpertOrderDetail(
  id: number,
): Promise<any> {
  const res = await peiwantHttp.get(`/api/massage/order/detail/${id}`);
  // BLW 响应格式：{ status: 200, data: {...} }
  if (res.data?.data && typeof res.data.data === "object") {
    return res.data.data;
  }
  throw new Error(res.data?.msg || "获取订单详情失败");
}
